import { BlockStack, Box, Card, InlineStack, Link, Spinner, Text } from '@shopify/polaris';
import { formatDate, formatToCurrency, postRequest, putRequest } from '../../../shared/components/functions';
import { FormattedHTML } from '../../../shared/components/FormattedHtml';
import { useEffect, useState } from 'react';

export const ReceiptCard = ({ expense, onComplete, setToast, setErrorToast }) => {
  const [S3ObjectUrl, setS3ObjectUrl] = useState();
  const [loading, setLoading] = useState({ approve: false, decline: false });
  const [animating, setAnimating] = useState(null);

  useEffect(() => {
    async function fetchS3ObjectUrl() {
      const s3Key = expense.s3_key;
      if (!s3Key || s3Key.length === 0) return setS3ObjectUrl(null);
      const data = await postRequest(`/api/s3-expenses`, { key: s3Key });
      setS3ObjectUrl(data);
    }
    if (expense.s3_key) fetchS3ObjectUrl();
  }, [expense.s3_key]);

  const approveReceipt = async (action) => {
    const isApprove = action === 'approve';
    setLoading({ ...loading, [action]: true });
    setAnimating(action);

    await putRequest(`/api/expenses/${expense.OnkostenId}`, { approved: isApprove }, setToast, setErrorToast);

    setTimeout(() => {
      setLoading({ approve: false, decline: false });
      setAnimating(null);
      onComplete();
    }, 300);
  };

  const cardStyle = {
    transition: 'transform 0.3s ease, opacity 0.3s ease',
    transform: animating === 'approve' ? 'translateX(100px)' : animating === 'decline' ? 'translateX(-100px)' : 'translateX(0)',
    opacity: animating ? 0 : 1
  };

  const actionButtonBase = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  };

  const declineButtonStyle = {
    ...actionButtonBase,
    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)'
  };

  const approveButtonStyle = {
    ...actionButtonBase,
    background: 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)'
  };

  return (
    <div style={cardStyle}>
      <Card>
        <BlockStack gap="500">
          {/* Header */}
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingLg" as="h2">
              Receipt #{expense.OnkostenId}
            </Text>
            <Box background="bg-fill-info" padding="200" borderRadius="200">
              <Text variant="headingMd" fontWeight="bold">
                {formatToCurrency(expense.Prijs)}
              </Text>
            </Box>
          </InlineStack>

          {/* Content Grid */}
          <InlineStack gap="600" align="start" wrap={false}>
            {/* Left side - Details */}
            <Box minWidth="300px">
              <BlockStack gap="300">
                <DetailRow label="Type" value={expense.expenseType} />
                <DetailRow label="Date" value={formatDate(expense.Datum)} />
                <DetailRow label="Timeslot" value={expense.startTechnician?.substring(0, 10)} />
                <DetailRow label="Tech time" value={`${expense.startTechnician?.substring(11, 16)} - ${expense.endTechnician?.substring(11, 16)}`} />
                <DetailRow label="Planned" value={`${expense.startPlanned?.substring(11, 16)} - ${expense.endPlanned?.substring(11, 16)}`} />

                {expense.timeslotRemarks && (
                  <Box paddingBlockStart="200">
                    <BlockStack gap="100">
                      <Text variant="bodySm" tone="subdued">
                        Remarks
                      </Text>
                      <FormattedHTML html={expense.timeslotRemarks} />
                    </BlockStack>
                  </Box>
                )}

                {expense.jobTypeId === '3' && (
                  <Box paddingBlockStart="200" borderBlockStartWidth="025" borderColor="border-subdued">
                    <BlockStack gap="300" paddingBlockStart="200">
                      <DetailRow
                        label="Job"
                        value={
                          <Link url={`/jobs/${expense.job}`} removeUnderline>
                            {expense.jobNumber}
                          </Link>
                        }
                      />
                      <DetailRow
                        label="Customer"
                        value={
                          <Link url={`/companies/${expense.jobCustomer}`} removeUnderline>
                            {expense.jobCustomerName}
                          </Link>
                        }
                      />
                      {expense.jobDescription && (
                        <Box>
                          <Text variant="bodySm" tone="subdued">
                            Description
                          </Text>
                          <FormattedHTML html={expense.jobDescription} />
                        </Box>
                      )}
                    </BlockStack>
                  </Box>
                )}
              </BlockStack>
            </Box>

            {/* Right side - Receipt Image */}
            <Box minWidth="200px">
              {S3ObjectUrl?.url ? (
                <a href={S3ObjectUrl.url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={S3ObjectUrl.url}
                    alt="receipt"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      cursor: 'zoom-in'
                    }}
                  />
                </a>
              ) : (
                <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                  <Text tone="subdued" alignment="center">
                    No receipt image
                  </Text>
                </Box>
              )}
            </Box>
          </InlineStack>

          {/* Action Buttons */}
          <Box paddingBlockStart="400">
            <InlineStack align="center" gap="1200">
              <button
                style={declineButtonStyle}
                disabled={loading.approve || loading.decline}
                type="button"
                onClick={() => approveReceipt('decline')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(238,90,90,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                aria-label="Decline expense"
              >
                {loading.decline ? (
                  <Spinner size="small" />
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                )}
              </button>

              <button
                style={approveButtonStyle}
                disabled={loading.approve || loading.decline}
                type="button"
                onClick={() => approveReceipt('approve')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(64,192,87,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                aria-label="Approve expense"
              >
                {loading.approve ? (
                  <Spinner size="small" />
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                )}
              </button>
            </InlineStack>
          </Box>
        </BlockStack>
      </Card>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <InlineStack gap="200" wrap={false}>
    <Box minWidth="90px">
      <Text variant="bodySm" tone="subdued">
        {label}
      </Text>
    </Box>
    <Text variant="bodyMd">{value}</Text>
  </InlineStack>
);
